import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { request } from '../api';
import { listEvents } from '../utils/eventsApi';
import { listProfiles, listReports } from '../utils/adminApi';
import ToastContext from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { FiCalendar, FiUsers, FiAward, FiFlag, FiActivity, FiPlus, FiArrowRight } from 'react-icons/fi';
import AppIcon from './icons/AppIcon';
import { isDemoAdmin, DEMO_ADMIN_MESSAGE } from '../utils/adminDemoMode';

export default function AdminDashboard() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalUsers: 0,
    mostPopularSport: null,
    activeEvents: 0,
    reports: 0,
    participationRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [animated, setAnimated] = useState(false);
  const toast = useContext(ToastContext);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const events = await listEvents();
      const [users, reports] = await Promise.all([listProfiles(), listReports().catch(() => [])]);

      // Calculer les statistiques
      const totalEvents = events.length;
      const totalUsers = users.length;
      const now = new Date();
      
      // Événements actifs
      const activeEvents = events.filter(e => {
        const eventDate = new Date(e.date);
        const eventEndDate = new Date(eventDate.getTime() + 24 * 60 * 60 * 1000);
        return eventDate <= now && now <= eventEndDate;
      }).length;

      // Sport le plus populaire
      const sportCount = {};
      events.forEach(event => {
        sportCount[event.sportType] = (sportCount[event.sportType] || 0) + 1;
      });

      let mostPopularSport = null;
      let maxCount = 0;
      for (const [sport, count] of Object.entries(sportCount)) {
        if (count > maxCount) {
          maxCount = count;
          mostPopularSport = sport;
        }
      }

      // Taux de participation
      const totalParticipants = events.reduce((sum, e) => sum + (e.participants?.length || 0), 0);
      const participationRate = totalUsers > 0 ? Math.round((totalParticipants / (totalUsers * events.length || 1)) * 100) : 0;

      setStats({
        totalEvents,
        totalUsers,
        mostPopularSport,
        activeEvents,
        reports: reports.length || 0,
        participationRate: Math.min(participationRate, 100)
      });

      // Trigger animation
      setTimeout(() => setAnimated(true), 100);
    } catch (err) {
      console.error(err);
      if (toast && toast.error) toast.error('Erreur lors du chargement des statistiques');
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    if (currentUser && currentUser.isAdmin) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [fetchStats, currentUser]);

  if (!currentUser || !currentUser.isAdmin) {
    return <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem' }}>Accès refusé. Vous devez être administrateur pour voir cette page.</div>;
  }

  if (loading) {
    return <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>Chargement du dashboard...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'transparent' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 1.5rem' }}>
        
        {/* Header Section */}
        <div style={{ marginBottom: '3rem', animation: 'slideUpFade 0.6s ease-out both' }}>
          <h1 style={{ 
            fontSize: 'clamp(2rem, 4vw, 2.8rem)', 
            fontWeight: 900, 
            marginBottom: '0.5rem',
            background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.5px'
          }}>
            Dashboard Administrateur
          </h1>
          <p style={{ fontSize: '1.05rem', color: 'var(--muted)', margin: 0, fontWeight: 500 }}>
            Vue d'ensemble de la plateforme
          </p>
          {currentUser.readOnly && (
            <span title="Votre accès administrateur est actuellement limité à la consultation." style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.45rem', marginTop: '1rem', padding: '0.45rem 0.7rem',
              color: 'var(--accent-2)', background: 'rgba(107,58,242,0.08)', border: '1px solid rgba(107,58,242,0.18)',
              borderRadius: '999px', fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.06em'
            }}>
              <span aria-hidden="true">&#128065;</span> MODE VUE
            </span>
          )}
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.75rem',
          marginBottom: '3rem'
        }}>
          {/* Événements Créés */}
          <StatCard 
            icon={<FiCalendar size={28} />}
            label="Événements créés"
            value={stats.totalEvents}
            color="accent"
            delay="0s"
            animated={animated}
          />

          {/* Utilisateurs Inscrits */}
          <StatCard 
            icon={<FiUsers size={28} />}
            label="Utilisateurs inscrits"
            value={stats.totalUsers}
            color="success"
            delay="0.1s"
            animated={animated}
          />

          {/* Événements Actifs */}
          <StatCard 
            icon={<FiActivity size={28} />}
            label="Événements actifs"
            value={stats.activeEvents}
            color="warning"
            delay="0.2s"
            animated={animated}
          />

          {/* Signalements */}
          <StatCard 
            icon={<FiFlag size={28} />}
            label="Signalements"
            value={stats.reports}
            color="danger"
            delay="0.3s"
            animated={animated}
          />

          {/* Taux de Participation */}
          <StatCard 
            icon={<FiAward size={28} />}
            label="Taux de participation"
            value={`${stats.participationRate}%`}
            color="purple"
            delay="0.4s"
            animated={animated}
          />

          {/* Sport Populaire */}
          <StatCard 
            icon={<AppIcon name="trophy" size={28} />}
            label="Sport le plus populaire"
            value={stats.mostPopularSport || 'N/A'}
            color="info"
            delay="0.5s"
            animated={animated}
            isText
          />
        </div>

        {/* Quick Actions Section */}
        <div style={{
          animation: 'slideUpFade 0.6s ease-out 0.3s both',
          marginBottom: '3rem'
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', margin: '0 0 0.25rem 0' }}>
              Actions Rapides
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: '0.95rem', margin: 0 }}>
              Accédez rapidement aux principales fonctionnalités
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1.25rem'
          }}>
            <ActionButton 
              to="/admin-users"
              icon={<AppIcon name="users" size={22} />}
              label="Gérer les utilisateurs"
              description="Voir et gérer tous les utilisateurs"
              color="accent"
            />
            <ActionButton 
              to="/admin-reports"
              icon={<AppIcon name="flag" size={22} />}
              label="Voir les signalements"
              description="Examiner les signalements reçus"
              color="danger"
            />
            <ActionButton 
              to="/events"
              icon={<AppIcon name="calendar" size={22} />}
              label="Voir les événements"
              description="Consulter tous les événements"
              color="success"
            />
            <ActionButton 
              to="/create-event"
              icon={<AppIcon name="target" size={22} />}
              label="Créer un événement"
              description="Ajouter un nouvel événement"
              color="info"
              blocked={isDemoAdmin(currentUser)}
              blockedMessage={DEMO_ADMIN_MESSAGE}
            />
            <ActionButton 
              to="/admin-challenges"
              icon={<AppIcon name="trophy" size={22} />}
              label="Gérer les défis"
              description="Créer et gérer les défis"
              color="warning"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// StatCard Component
function StatCard({ icon, label, value, color, delay, animated, isText }) {
  const colorMap = {
    accent: { bg: 'rgba(56, 189, 248, 0.1)', border: 'rgba(56, 189, 248, 0.2)', text: 'var(--accent)' },
    success: { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.2)', text: '#10b981' },
    warning: { bg: 'rgba(251, 191, 36, 0.1)', border: 'rgba(251, 191, 36, 0.2)', text: '#fbbf24' },
    danger: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)', text: '#ef4444' },
    purple: { bg: 'rgba(168, 85, 247, 0.1)', border: 'rgba(168, 85, 247, 0.2)', text: '#a855f7' },
    info: { bg: 'rgba(6, 182, 212, 0.1)', border: 'rgba(6, 182, 212, 0.2)', text: '#06b6d4' }
  };

  const colors = colorMap[color] || colorMap.accent;

  return (
    <div
      style={{
        padding: '2rem',
        borderRadius: '20px',
        background: colors.bg,
        border: `1.5px solid ${colors.border}`,
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        animation: `slideUpFade 0.6s ease-out ${delay} both`,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '200px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = `0 16px 40px ${colors.text}20`;
        e.currentTarget.style.borderColor = colors.text;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = colors.border;
      }}
    >
      {/* Icon */}
      <div style={{ 
        fontSize: typeof icon === 'string' ? '2.5rem' : '1.5rem',
        marginBottom: '1rem',
        color: colors.text,
        display: 'flex',
        alignItems: 'center'
      }}>
        {icon}
      </div>

      {/* Value with Animation */}
      <div>
        <div style={{
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          fontWeight: 900,
          color: colors.text,
          marginBottom: '0.75rem',
          letterSpacing: '-0.3px'
        }}>
          {typeof value === 'number' && animated ? <AnimatedCounter value={value} /> : value}
        </div>

        {/* Label */}
        <p style={{
          fontSize: '0.95rem',
          fontWeight: 600,
          color: 'var(--muted)',
          margin: 0,
          textTransform: isText ? 'none' : 'capitalize'
        }}>
          {label}
        </p>
      </div>
    </div>
  );
}

// ActionButton Component
function ActionButton({ to, icon, label, description, color, blocked, blockedMessage }) {
  const toast = useContext(ToastContext);
  const colorMap = {
    accent: { bg: 'rgba(56, 189, 248, 0.08)', border: 'rgba(56, 189, 248, 0.3)', text: 'var(--accent)' },
    success: { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.3)', text: '#10b981' },
    warning: { bg: 'rgba(251, 191, 36, 0.08)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24' },
    danger: { bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.3)', text: '#ef4444' },
    info: { bg: 'rgba(6, 182, 212, 0.08)', border: 'rgba(6, 182, 212, 0.3)', text: '#06b6d4' }
  };

  const colors = colorMap[color] || colorMap.accent;

  return (
    <Link
      to={to}
      onClick={(event) => {
        if (blocked) {
          event.preventDefault();
          toast?.info(blockedMessage);
        }
      }}
      style={{
        display: 'block',
        padding: '1.5rem',
        borderRadius: '18px',
        background: colors.bg,
        border: `1.5px solid ${colors.border}`,
        textDecoration: 'none',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        cursor: 'pointer',
        animation: 'slideUpFade 0.6s ease-out 0.4s both'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = `0 12px 32px ${colors.text}15`;
        e.currentTarget.style.borderColor = colors.text;
        e.currentTarget.style.background = `${colors.bg}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = colors.border;
        e.currentTarget.style.background = colors.bg;
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{icon}</div>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: colors.text, margin: '0 0 0.25rem 0' }}>
            {label}
          </h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--muted)', margin: 0 }}>
            {description}
          </p>
        </div>
        <div style={{ 
          fontSize: '1.5rem', 
          color: colors.text, 
          opacity: 0.5,
          transition: 'all 0.3s ease'
        }}>
          <FiArrowRight size={24} />
        </div>
      </div>
    </Link>
  );
}

// AnimatedCounter Component
function AnimatedCounter({ value }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = value / 30;
    const interval = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(interval);
      } else {
        setCount(Math.floor(start));
      }
    }, 30);

    return () => clearInterval(interval);
  }, [value]);

  return <>{count}</>;
}